import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, XCircle, Trash2, Building2, Users as UsersIcon, ClipboardList, ShieldAlert } from "lucide-react";

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })
    .format(n || 0);

const STATUS_PILLS = {
  pending: "bg-amber-100 text-amber-800 border border-amber-200",
  approved: "bg-emerald-100 text-emerald-800 border border-emerald-200",
  rejected: "bg-rose-100 text-rose-800 border border-rose-200",
  cancelled: "bg-slate-100 text-slate-700 border border-slate-200",
};

export default function DeletionRequests() {
  const { user } = useAuth();
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decideRow, setDecideRow] = useState(null);
  const [decision, setDecision] = useState("approve");

  const isAdmin = user?.role === "crm_manager";

  const load = async () => {
    setLoading(true);
    try {
      const params = tab === "all" ? {} : { status: tab };
      const { data } = await api.get("/deletion-requests", { params });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const onCancel = async (req) => {
    if (!window.confirm("Cancel this deletion request?")) return;
    try {
      await api.delete(`/deletion-requests/${req.id}`);
      toast.success("Request cancelled");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-6 md:p-8 space-y-4" data-testid="deletion-requests-page">
      <div>
        <div className="mptg-overline">Approvals</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1 flex items-center gap-2">
          <ShieldAlert className="w-7 h-7 text-[#C0392B]" /> Deletion Requests
        </h1>
        <p className="text-slate-500 mt-1">
          {isAdmin
            ? "Review deletion requests raised by Branch Managers. Outstanding billing blocks client deletion."
            : "Track the status of your submitted deletion requests."}
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="dr-tabs">
          <TabsTrigger value="pending" data-testid="dr-tab-pending">Pending</TabsTrigger>
          <TabsTrigger value="approved" data-testid="dr-tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="dr-tab-rejected">Rejected</TabsTrigger>
          <TabsTrigger value="cancelled" data-testid="dr-tab-cancelled">Cancelled</TabsTrigger>
          <TabsTrigger value="all" data-testid="dr-tab-all">All</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-3">
          <div className="mptg-card p-0 overflow-hidden">
            <table className="w-full mptg-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Record</th>
                  <th>Outstanding</th>
                  <th>Reason</th>
                  <th>Requested by</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="py-10 text-center text-slate-400">Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={8} className="py-10 text-center text-slate-400">No requests in this view.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id} data-testid={`dr-row-${r.id}`}>
                    <td>
                      <span className="text-xs inline-flex items-center gap-1 text-slate-700">
                        {r.entity_type === "client"
                          ? <UsersIcon className="w-3.5 h-3.5 text-[#2E86C1]" />
                          : <Building2 className="w-3.5 h-3.5 text-[#2E86C1]" />}
                        {r.entity_type}
                      </span>
                    </td>
                    <td className="font-medium">{r.entity_label || <span className="text-slate-400 italic">deleted</span>}</td>
                    <td className="tabular-nums text-xs">
                      {r.entity_type === "client"
                        ? <span className={r.outstanding_amount > 0 ? "text-[#C0392B] font-semibold" : "text-slate-500"}>{inr(r.outstanding_amount || 0)}</span>
                        : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="text-xs text-slate-600 max-w-xs truncate" title={r.reason}>{r.reason || "-"}</td>
                    <td className="text-xs">
                      {r.requested_by_name || "-"}
                      {r.requested_by_email && <div className="text-slate-400">{r.requested_by_email}</div>}
                    </td>
                    <td className="text-xs text-slate-500">{(r.requested_at || "").slice(0, 10)}</td>
                    <td>
                      <span className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${STATUS_PILLS[r.status] || ""}`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="text-right whitespace-nowrap space-x-3">
                      {r.status === "pending" && isAdmin && (
                        <>
                          <button
                            onClick={() => { setDecideRow(r); setDecision("approve"); }}
                            className="text-[#1E8449] hover:underline text-xs inline-flex items-center gap-1"
                            data-testid={`dr-approve-${r.id}`}
                            disabled={r.entity_type === "client" && (r.outstanding_amount || 0) > 0}
                            title={r.entity_type === "client" && (r.outstanding_amount || 0) > 0 ? "Outstanding billing must be settled first" : ""}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                          </button>
                          <button
                            onClick={() => { setDecideRow(r); setDecision("reject"); }}
                            className="text-[#C0392B] hover:underline text-xs inline-flex items-center gap-1"
                            data-testid={`dr-reject-${r.id}`}
                          >
                            <XCircle className="w-3.5 h-3.5" /> Reject
                          </button>
                        </>
                      )}
                      {r.status === "pending" && !isAdmin && r.requested_by === user?.id && (
                        <button
                          onClick={() => onCancel(r)}
                          className="text-slate-600 hover:underline text-xs inline-flex items-center gap-1"
                          data-testid={`dr-cancel-${r.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Cancel
                        </button>
                      )}
                      {r.status !== "pending" && r.decided_by_name && (
                        <span className="text-xs text-slate-400">by {r.decided_by_name}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {decideRow && (
        <Dialog open={true} onOpenChange={(v) => !v && setDecideRow(null)}>
          <DecideDialog row={decideRow} decision={decision} onDone={() => { setDecideRow(null); load(); }} />
        </Dialog>
      )}
    </div>
  );
}

function DecideDialog({ row, decision, onDone }) {
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const isApprove = decision === "approve";

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/deletion-requests/${row.id}/${decision}`, { decision_notes: notes });
      toast.success(isApprove ? "Approved & deleted" : "Request rejected");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <DialogContent className="max-w-md" data-testid="dr-decide-dialog">
      <DialogHeader>
        <DialogTitle>{isApprove ? "Approve & delete" : "Reject"} — {row.entity_label}</DialogTitle>
      </DialogHeader>
      <div className="space-y-3 py-2 text-sm">
        <div className="text-slate-600">
          <span className="font-semibold">Type:</span> {row.entity_type} ·{" "}
          <span className="font-semibold">Requested by:</span> {row.requested_by_name || "-"}
        </div>
        <div>
          <span className="font-semibold text-slate-600">Reason:</span>
          <div className="text-slate-700 mt-1 p-2 bg-slate-50 rounded border border-slate-100 text-xs">
            {row.reason || "—"}
          </div>
        </div>
        {isApprove && row.entity_type === "client" && (row.outstanding_amount || 0) > 0 && (
          <div className="text-xs p-2 bg-rose-50 border border-rose-200 rounded text-rose-800">
            Outstanding billing of {inr(row.outstanding_amount)} — approval will be blocked.
          </div>
        )}
        <div>
          <Label>Decision notes (optional)</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="dr-decision-notes" />
        </div>
      </div>
      <DialogFooter>
        <Button
          onClick={submit}
          disabled={busy}
          className={isApprove ? "bg-[#1E8449] hover:bg-[#1a6e3e] text-white" : "bg-[#C0392B] hover:bg-[#A93226] text-white"}
          data-testid="dr-confirm-decision"
        >
          {busy ? "Submitting…" : isApprove ? "Approve & delete" : "Reject"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
